import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { EventAchievementCard } from "./EventAchievementCard";
import { Share2, User } from "lucide-react";

interface EventAchievementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  achievementType: 'first_event' | 'attended_5' | 'attended_10' | 'attended_25' | 'attended_50' | 'attended_100';
  username: string;
  earnedDate: string;
}

export const EventAchievementDialog = ({
  open,
  onOpenChange,
  achievementType,
  username,
  earnedDate,
}: EventAchievementDialogProps) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      // Trigger confetti animation
      const duration = 3000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 1000 };

      const randomInRange = (min: number, max: number) => {
        return Math.random() * (max - min) + min;
      };

      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          clearInterval(interval);
          return;
        }

        const particleCount = 50 * (timeLeft / duration);

        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        });
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        });
      }, 250);

      return () => clearInterval(interval);
    }
  }, [open]);

  const handleShare = () => {
    // The EventAchievementCard has its own share functionality
    onOpenChange(false);
  };

  const handleViewProfile = () => {
    onOpenChange(false);
    navigate('/profile');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl">
            🎉 Achievement Unlocked!
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <EventAchievementCard
            achievementType={achievementType}
            username={username}
            earnedDate={earnedDate}
          />
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1 gap-2"
              onClick={handleShare}
            >
              <Share2 className="w-4 h-4" />
              Share
            </Button>
            <Button
              className="flex-1 gap-2"
              onClick={handleViewProfile}
            >
              <User className="w-4 h-4" />
              View Profile
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};